import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, ContentEntity, ContentNode, HasChildren } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { ContentEntityContent, ResponsiveContainer } from '@shapeable/ui';
import { BLOCK_METRIC_HEADING, CONTENT_BACKGROUND, OCEAN_BLUE } from '../../theme';

// -------- Types -------->

export type MetricLayoutProps = Classable & HasChildren & { 
  entity?: ContentEntity;
  ratio?: number;
  showContent?: boolean;
  controls?: React.ReactNode;
  hasPadding?: boolean;
  stretch?: boolean;
};

export const MetricLayoutDefaultProps: Partial<MetricLayoutProps> = {
  ratio: 1,
  showContent: false,
  hasPadding: true,
  stretch: true,
};

// -------- Child Component Props -------->

type ContainerProps = {
  ratio: number;
  stretch: boolean;
}

type BodyProps = {
  hasPadding?: boolean;
}
// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    background-color: ${CONTENT_BACKGROUND};
    font-size: ${theme.FONT_SIZE(13)};

    ${(props: any) => (props.stretch || props.ratio !== 1) && css`
      flex-grow: 1;
      height: ${100 / props.ratio}%;
    `}

    ${(props: any) => props.ratio !== 1 && css`
      border-bottom: 8px solid ${theme.COLOR('light')};

      &:last-child {
        border-bottom: none;
        margin-bottom: -4px;
      }
    `}

    position: relative;
    box-sizing: border-box;
    .shp--responsive-container__body {
      position: relative;
    }
  `,
  tablet: css`
    font-size: ${theme.FONT_SIZE(14)};
  `,
  desktop: css`
    font-size: ${theme.FONT_SIZE(15)};
  `,
});

const BodyStyles = breakpoints({
  base: css`
    display: flex;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    flex-direction: column;

    ${({ hasPadding }: BodyProps) => hasPadding && css`
      padding: ${theme.UNIT(4)} ${theme.UNIT(3)} ${theme.UNIT(3)} ${theme.UNIT(3)};
    `}
    `
});

const TitleStyles = breakpoints({
  base: css`
    font-size: 1em;
    margin: 0 0 ${theme.UNIT(2)} 0;
    text-transform: uppercase;
    font-weight: 300;
    color: ${BLOCK_METRIC_HEADING};
  `,
});

const ContentStyles = breakpoints({
  base: css`
    font-size: 1em;

    b, strong {
      color: ${OCEAN_BLUE};
    }
  `,
  tablet: css`
    min-height: 3em;
  `
});

const ControlsStyles = breakpoints({
  base: css`
    box-sizing: border-box;
    margin-top: ${theme.UNIT(4)};
    width: 100%;
    display: flex;
    flex-direction: row;
  `,
  tablet: css`
    position: absolute;
    bottom: 0px;
    left: 0;
    padding: ${theme.UNIT(3)};
  `
});

// -------- Components -------->

const My = {
  Container: styled(ResponsiveContainer)<ContainerProps>`${ContainerStyles}`,
  Body: styled.div<BodyProps>`${BodyStyles}`,
    Title: styled.h3`${TitleStyles}`,
    Content: styled(ContentEntityContent)`${ContentStyles}`,
  Controls: styled.div`${ControlsStyles}`,
};

export const MetricLayout: React.FC<MetricLayoutProps> = (props) => {
  const { className, children, entity, showContent, controls, ratio, stretch, hasPadding } = props;

  const { title, content } = entity || {};

  const hasTitle = !!title;
  const hasContent = !!content;

  return (
    <My.Container ratio={ratio} stretch={stretch} fill={false} aspectRatio={{ base: null, tablet: (stretch || ratio !== 1) ? ratio : null }} alignItems='flex-start' justifyContent='flex-start' className={className}>
      <My.Body hasPadding={hasPadding}>
        {
          hasTitle && <My.Title>{title}</My.Title>
        }
        {
          showContent && hasContent && <My.Content entity={entity} />
        }

        {children}

        {
          controls && <My.Controls>{controls}</My.Controls>
        }
      </My.Body>      
    </My.Container>
  )
};

MetricLayout.defaultProps = MetricLayoutDefaultProps;
